import { Button, DatePicker, Modal, TimePicker } from '@nexo/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDay, formatTime, isoDateFromToday } from '../lib/format';

/** Tomorrow (or `days` from today) at `hour`:00, local time. */
function dayAt(days: number, hour: number): Date {
  const date = new Date();
  date.setDate(date.getDate() + days);
  date.setHours(hour, 0, 0, 0);
  return date;
}

/** Next Monday at 9:00. */
function nextMonday(): Date {
  const today = new Date().getDay();
  return dayAt((8 - today) % 7 || 7, 9);
}

/** The shortcuts offered everywhere a moment is chosen (reminders, scheduled messages). */
export function whenPresets(): { label: string; at: Date }[] {
  const now = Date.now();
  return [
    { label: 'En 20 minutos', at: new Date(now + 20 * 60_000) },
    { label: 'En 1 hora', at: new Date(now + 60 * 60_000) },
    { label: 'En 3 horas', at: new Date(now + 3 * 60 * 60_000) },
    { label: 'Mañana a las 9:00', at: dayAt(1, 9) },
    { label: 'El lunes a las 9:00', at: nextMonday() },
  ];
}

/** «When?»: quick choices or a custom day and time. Calls `onPick` with an ISO timestamp. */
export function WhenDialog({
  title,
  description,
  confirmLabel,
  loading,
  onPick,
  onClose,
}: {
  title: string;
  description?: string;
  confirmLabel: string;
  loading?: boolean;
  onPick: (iso: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [day, setDay] = useState<string | null>(isoDateFromToday(1));
  const [time, setTime] = useState('09:00');
  // Worked out once when the dialog opens.
  const [presets] = useState(whenPresets);
  const [error, setError] = useState<string | null>(null);

  function pickCustom() {
    if (!day) return;
    const at = new Date(`${day}T${time}`);
    if (at.getTime() <= Date.now() + 60_000) return setError(t('Elige un momento en el futuro'));
    onPick(at.toISOString());
  }

  return (
    <Modal open onClose={onClose} size="sm" title={title} description={description}>
      <div className="flex flex-col gap-1">
        {presets.map((preset) => (
          <button
            key={preset.label}
            type="button"
            disabled={loading}
            onClick={() => onPick(preset.at.toISOString())}
            className="flex items-center justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-surface-2 disabled:opacity-50"
          >
            <span>{t(preset.label)}</span>
            <span className="text-xs text-muted">
              {formatDay(preset.at.toISOString())} · {formatTime(preset.at.toISOString())}
            </span>
          </button>
        ))}
      </div>
      <div className="mt-4 border-t border-border pt-4">
        <p className="mb-2 text-sm font-medium">{t('Otro momento')}</p>
        <div className="grid grid-cols-[1fr_auto] gap-2">
          <DatePicker
            aria-label={t('Día')}
            value={day}
            onChange={(value) => {
              setDay(value);
              setError(null);
            }}
            min={isoDateFromToday(0)}
          />
          <TimePicker
            aria-label={t('Hora')}
            value={time}
            onChange={(value) => {
              setTime(value);
              setError(null);
            }}
          />
        </div>
        {error && <p className="mt-2 text-xs text-danger">{error}</p>}
        <Button className="mt-3 w-full" disabled={!day} loading={loading} onClick={pickCustom}>
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}
